import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AgentHeader } from "@/components/AgentHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageLoader } from "@/components/shared";
import { Plus, Pencil, Search, ExternalLink, Loader2 } from "lucide-react";

const STATUSES = [
  { value: "pending_review", label: "Pending", cls: "bg-status-warning/15 text-status-warning" },
  { value: "approved", label: "Approved", cls: "bg-status-complete/15 text-status-complete" },
  { value: "official", label: "Official", cls: "bg-forest/15 text-forest" },
  { value: "hidden", label: "Hidden", cls: "bg-destructive/15 text-destructive" },
];
const statusMeta = (s) => STATUSES.find((x) => x.value === s) || { label: s, cls: "bg-secondary text-muted-foreground" };

const EMPTY = { category: "", stages: "", type: "vendor", name: "", phone: "", website: "", service_area: "", description: "", license_shown_on_site: "", note: "", status: "pending_review" };

function VendorDialog({ open, onOpenChange, initial, onSaved }) {
  const qc = useQueryClient();
  const isEdit = !!initial?.id;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setF(initial?.id
        ? { ...EMPTY, ...initial, stages: (initial.stages || []).join(", ") }
        : EMPTY);
    }
  }, [open, initial]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!f.name.trim() || !f.category.trim()) return toast.error("Name and category are required");
    setBusy(true);
    const payload = { ...f, stages: f.stages.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (isEdit) await api.put(`/agent/vendors/${initial.id}`, payload);
      else await api.post("/agent/vendors", payload);
      toast.success(isEdit ? "Vendor updated" : "Vendor added");
      qc.invalidateQueries({ queryKey: ["agent-vendors"] });
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="vendor-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={set("name")} className="h-10 rounded-xl" data-testid="vf-name" /></div>
          <div className="space-y-1.5"><Label>Category</Label><Input value={f.category} onChange={set("category")} className="h-10 rounded-xl" data-testid="vf-category" /></div>
          <div className="space-y-1.5"><Label>Type</Label><Input value={f.type} onChange={set("type")} placeholder="vendor or official_resource" className="h-10 rounded-xl" data-testid="vf-type" /></div>
          <div className="space-y-1.5"><Label>Status</Label><Input value={f.status} onChange={set("status")} placeholder="pending_review / approved / official / hidden" className="h-10 rounded-xl" data-testid="vf-status" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Stages (comma-separated)</Label><Input value={f.stages} onChange={set("stages")} placeholder="Buying, Selling, New homeowner, Moving to Oahu / military" className="h-10 rounded-xl" data-testid="vf-stages" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone || ""} onChange={set("phone")} className="h-10 rounded-xl" data-testid="vf-phone" /></div>
          <div className="space-y-1.5"><Label>Website</Label><Input value={f.website || ""} onChange={set("website")} className="h-10 rounded-xl" data-testid="vf-website" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Service area</Label><Input value={f.service_area || ""} onChange={set("service_area")} className="h-10 rounded-xl" data-testid="vf-service-area" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea value={f.description || ""} onChange={set("description")} className="rounded-xl" data-testid="vf-description" /></div>
          <div className="space-y-1.5"><Label>License shown on site</Label><Input value={f.license_shown_on_site || ""} onChange={set("license_shown_on_site")} className="h-10 rounded-xl" data-testid="vf-license" /></div>
          <div className="space-y-1.5"><Label>Note (private)</Label><Input value={f.note || ""} onChange={set("note")} className="h-10 rounded-xl" data-testid="vf-note" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="rounded-full gap-2" data-testid="vf-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? "Save changes" : "Add vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VendorManager() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const vQ = useQuery({ queryKey: ["agent-vendors"], queryFn: async () => (await api.get("/agent/vendors")).data });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/agent/vendors/${id}`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-vendors"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const vendors = vQ.data?.vendors || [];
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return vendors;
    return vendors.filter((v) => [v.name, v.category, v.description, v.service_area, v.status].filter(Boolean).join(" ").toLowerCase().includes(query));
  }, [vendors, q]);

  const counts = useMemo(() => {
    const c = { pending_review: 0, approved: 0, official: 0, hidden: 0 };
    vendors.forEach((v) => { c[v.status] = (c[v.status] || 0) + 1; });
    return c;
  }, [vendors]);

  return (
    <div className="min-h-screen bg-background">
      <AgentHeader />
      <main className="max-w-6xl mx-auto p-4 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="font-heading text-2xl font-bold">Vendor Manager</h1>
            <p className="text-sm text-muted-foreground mt-1">Built-in vendor guide shown on every client's portal. Only <b>Approved</b> and <b>Official</b> entries are visible to clients.</p>
          </div>
          <Button onClick={() => setDlg({ open: true, initial: null })} className="rounded-full gap-2" data-testid="vendor-add-btn"><Plus className="h-4 w-4" /> Add vendor</Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          {STATUSES.map((s) => <span key={s.value} className={`px-2.5 py-1 rounded-full font-medium ${s.cls}`}>{s.label}: {counts[s.value] || 0}</span>)}
        </div>

        <div className="relative mb-5 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all vendors…" className="pl-9 h-11 rounded-xl" data-testid="vendor-manager-search" />
        </div>

        {vQ.isLoading ? <PageLoader /> : (
          <div className="space-y-3">
            {filtered.map((v) => {
              const sm = statusMeta(v.status);
              return (
                <Card key={v.id} className="p-4 rounded-2xl" data-testid={`vm-row-${v.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{v.name}</p>
                        <Badge variant="outline" className="text-xs">{v.category}</Badge>
                        <Badge className={`border-0 text-xs ${sm.cls}`} data-testid={`vm-status-${v.id}`}>{sm.label}</Badge>
                        {v.type === "official_resource" && <Badge variant="outline" className="text-xs">official_resource</Badge>}
                      </div>
                      {v.description && <p className="text-sm text-muted-foreground mt-1">{v.description}</p>}
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {v.service_area && <span>Serves: {v.service_area}</span>}
                        {v.phone && <span>{v.phone}</span>}
                        {v.license_shown_on_site && <span>License {v.license_shown_on_site}</span>}
                        {(v.stages || []).length > 0 && <span>Stages: {v.stages.join(", ")}</span>}
                        {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-0.5 hover:underline">site <ExternalLink className="h-3 w-3" /></a>}
                      </div>
                      {v.note && <p className="text-xs mt-2 p-2 rounded-lg bg-amber-100/50 text-amber-800" data-testid={`vm-note-${v.id}`}>📝 {v.note}</p>}
                    </div>
                    <Button variant="outline" size="sm" className="rounded-full gap-1 h-8" onClick={() => setDlg({ open: true, initial: v })} data-testid={`vm-edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {STATUSES.map((s) => (
                      <button key={s.value} onClick={() => v.status !== s.value && setStatus.mutate({ id: v.id, status: s.value })}
                        data-testid={`vm-set-${s.value}-${v.id}`}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${v.status === s.value ? s.cls + " ring-1 ring-current" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <VendorDialog open={dlg.open} onOpenChange={(o) => setDlg((p) => ({ ...p, open: o }))} initial={dlg.initial} />
    </div>
  );
}
